import React, { PureComponent } from 'react';
import { BaseInputProps } from '../types';
import classNames from 'classnames';
import { isExpired, isNullLike } from '../utils';
import { Num, NumCvt } from './Num';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Expiries } from './Expiries';
import { TextSelector } from './TextSelector';
import { capitalize } from 'lodash-es';
import { Bool } from './Bool';
import { Checkbox, Select } from 'antd';
import { NumRange } from './NumRange';
import { NumRangeFromTo } from './NumRangeFromTo';
import { SettleContext } from '../settleContext';

interface DeltaRange {
  min?: number;
  max?: number;
}

export interface VegaRule {
  optionType?: string;
  deltaRange: DeltaRange;
  bidEdgeSpan: {
    from?: number;
    to?: number;
  };
  askEdgeSpan: {
    from?: number;
    to?: number;
  };
  allowTaker: boolean;
  tob: boolean;
  disable: boolean;
}

export interface ExpiryVegaAlphaByEdgeInfo {
  expiry?: string;
  vegaTarget?: number;
  vegaRules: VegaRule[];
}

function hasRangeOverlap(range1: DeltaRange, range2: DeltaRange): boolean {
  const min1 = range1.min ?? -Infinity;
  const max1 = range1.max ?? Infinity;
  const min2 = range2.min ?? -Infinity;
  const max2 = range2.max ?? Infinity;

  // 边界相等不算重叠，所以用严格比较
  return min1 < max2 && min2 < max1;
}

/**
 * 检查VegaRules数组是否合法
 */
export function validateVegaRules(rules: VegaRule[]): boolean {
  for (let i = 0; i < rules.length; i++) {
    const currentRule = rules[i];
    const currentType = currentRule.optionType ?? 'Both'; // 默认按Both处理

    for (let j = i + 1; j < rules.length; j++) {
      const compareRule = rules[j];
      const compareType = compareRule.optionType ?? 'Both';

      // 检查是否需要比较这两个规则
      let shouldCompare = false;

      if (currentType === 'Both' || compareType === 'Both') {
        // 任意一个是Both，就需要比较
        shouldCompare = true;
      } else if (currentType === compareType) {
        // 类型相同（都是Call或都是Put），需要比较
        shouldCompare = true;
      }
      // 类型不同（一个Call一个Put），不需要比较

      if (shouldCompare && hasRangeOverlap(currentRule.deltaRange, compareRule.deltaRange)) {
        return false; // 发现重叠，不合法
      }
    }
  }

  return true; // 没有发现重叠，合法
}

export interface ExpiryVegaAlphaByEdgeProps extends BaseInputProps {
  value?: ExpiryVegaAlphaByEdgeInfo[];

  onChange?: (val: ExpiryVegaAlphaByEdgeInfo[]) => void;

  options?: string[];
}

export class ExpiryVegaAlphaByEdgeList extends PureComponent<ExpiryVegaAlphaByEdgeProps> {
  // 结算上下文：由 ValueDefaultsSelector 按 GLOBAL/ASSET/BOT 作用域注入，用于 expiry 到期判断
  static contextType = SettleContext;
  declare context: React.ContextType<typeof SettleContext>;

  value: ExpiryVegaAlphaByEdgeInfo[] = [];

  isEmptyItem = (item: ExpiryVegaAlphaByEdgeInfo) => {
    const vegaRulesValid = item.vegaRules
      ? item.vegaRules?.find(
          (it) =>
            it.deltaRange.min ||
            it.deltaRange.max ||
            it.bidEdgeSpan.from ||
            it.bidEdgeSpan.to ||
            it.askEdgeSpan.from ||
            it.askEdgeSpan.to,
        )
      : true;
    return !item.expiry?.trim() && !item.vegaTarget && !vegaRulesValid;
  };

  onChange = (
    i: number,
    val?: any,
    key?: keyof ExpiryVegaAlphaByEdgeInfo,
    k?: number,
    subKey?: keyof VegaRule,
  ) => {
    const value = [...this.value];
    if (!value[i])
      value[i] = {
        expiry: undefined,
        vegaTarget: undefined,
        vegaRules: [
          {
            optionType: undefined,
            deltaRange: {
              min: undefined,
              max: undefined,
            },
            bidEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            askEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            allowTaker: false,
            tob: false,
            disable: false,
          },
        ],
      };

    if (key) {
      if (subKey && typeof k === 'number') {
        // if (subKey === 'optionType') {
        //   // console.log('changeValue==========================', value, i, val, key, k, subKey);

        //   if (val === 'Both') {
        //     value[i].vegaRules = [{ ...value[i].vegaRules[k], optionType: 'Both' }];
        //   } else {
        //     if (value[i].vegaRules?.length <= 1)
        //       value[i].vegaRules = [
        //         { ...value[i].vegaRules[k], optionType: val },
        //         {
        //           optionType: val === 'Call' ? 'Put' : 'Call',
        //           deltaRange: {
        //             min: undefined,
        //             max: undefined,
        //           },
        //           bidEdgeSpan: {
        //             from: undefined,
        //             to: undefined,
        //           },
        //           askEdgeSpan: {
        //             from: undefined,
        //             to: undefined,
        //           },
        //           allowTaker: false,
        //           disable: false,
        //         },
        //       ];
        //     else value[i].vegaRules[k][subKey] = val as never;
        //   }
        // } else {
        //   value[i].vegaRules[k][subKey] = val as never;
        // }
        value[i].vegaRules[k][subKey] = val as never;
      } else {
        value[i][key] = val as never;
      }
    } else {
      value[i] = { ...value[i], ...val };
    }

    this.props.onChange?.(value.length === 1 && this.isEmptyItem(value[0]) ? [] : value);
  };

  onAdd = (i: number) => {
    const value = [...this.value];
    value.splice(i + 1, 0, {
      expiry: undefined,
      vegaTarget: undefined,
      vegaRules: [
        {
          optionType: undefined,
          deltaRange: {
            min: undefined,
            max: undefined,
          },
          bidEdgeSpan: {
            from: undefined,
            to: undefined,
          },
          askEdgeSpan: {
            from: undefined,
            to: undefined,
          },
          allowTaker: false,
          tob: false,
          disable: false,
        },
      ],
    });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };
  onAddRule = (i: number, k: number) => {
    const value = [...this.value];
    value[i].vegaRules.splice(k + 1, 0, {
      optionType: undefined,
      deltaRange: {
        min: undefined,
        max: undefined,
      },
      bidEdgeSpan: {
        from: undefined,
        to: undefined,
      },
      askEdgeSpan: {
        from: undefined,
        to: undefined,
      },
      allowTaker: false,
      tob: false,
      disable: false,
    });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };

  onDel = (i: number) => {
    const value = [...this.value];
    value.splice(i, 1);
    this.props.onChange?.(value.length === 1 && this.isEmptyItem(value[0]) ? [] : value);
    this.props.onBlur?.();
  };

  onDelRule = (i: number, k: number) => {
    const value = [...this.value];
    value[i].vegaRules.splice(k, 1);
    this.props.onChange?.(value.length === 1 && this.isEmptyItem(value[0]) ? [] : value);
    this.props.onBlur?.();
  };

  render() {
    this.value = this.props.value?.filter((it) => !isExpired(it.expiry, this.context)) || [];
    if (!this.value.length)
      this.value.push({
        expiry: undefined,
        vegaTarget: undefined,
        vegaRules: [
          {
            optionType: undefined,
            deltaRange: {
              min: undefined,
              max: undefined,
            },
            bidEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            askEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            allowTaker: false,
            tob: false,
            disable: false,
          },
        ],
      });
    return (
      <div
        className={classNames(
          this.props.className,
        )}
        style={this.props.style}
      >
        {this.props.readonly ? (
          this.props.value?.length ? (
            this.value
              .filter((it) => it.expiry)
              .map((it, i) => (
                <>
                  <span key={i}>
                    {it.expiry}
                    <span>/</span>
                    {!isNullLike(it.vegaTarget) ? it.vegaTarget : '-'}
                    <span>/</span>
                    {it.vegaRules.map(($it, $i) => (
                      <>
                        <span key={$i}>
                          {!isNullLike($it.optionType) ? $it.optionType : '-'}
                          <span>/</span>
                          {!isNullLike($it.deltaRange.min) && !isNullLike($it.deltaRange.max)
                            ? `(${$it.deltaRange.min}, ${$it.deltaRange.max})`
                            : '-'}
                          <span>/</span>
                          {!isNullLike($it.bidEdgeSpan.from) && !isNullLike($it.bidEdgeSpan.to)
                            ? `(${NumCvt.show($it.bidEdgeSpan.from, true)}%, ${NumCvt.show(
                                $it.bidEdgeSpan.to,
                                true,
                              )}%)`
                            : '-'}
                          <span>/</span>
                          {!isNullLike($it.askEdgeSpan.from) && !isNullLike($it.askEdgeSpan.to)
                            ? `(${NumCvt.show($it.askEdgeSpan.from, true)}%, ${NumCvt.show(
                                $it.askEdgeSpan.to,
                                true,
                              )}%)`
                            : '-'}
                          <span>/</span>
                          {typeof $it.allowTaker === 'boolean'
                            ? $it.allowTaker.toString().toUpperCase()
                            : '-'}
                            <span>/</span>
                          {typeof $it.tob === 'boolean'
                            ? $it.tob.toString().toUpperCase()
                            : '-'}
                          <span>/</span>
                          {typeof $it.disable === 'boolean'
                            ? $it.disable.toString().toUpperCase()
                            : '-'}
                        </span>
                        <br />
                      </>
                    ))}
                  </span>
                  <br />
                </>
              ))
          ) : (
            '-'
          )
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 1150 }}
          >
            {/* 表头 */}
            <div style={{ display: 'flex', width: '100%', gap: 8, color: '#374151', fontWeight: 500 }}>
              <div style={{ width: 100 }}>Expiry</div>
              <div style={{ width: 80 }}>Vega Target</div>
              <div style={{ width: 120, paddingLeft: 18 }}>Both/Call/Put</div>
              <div style={{ width: 160 }}>Delta Range</div>
              <div style={{ width: 200 }}>Bid Edge Range</div>
              <div style={{ width: 200 }}>Ask Edge Range</div>
              <div style={{ width: 74, textAlign: 'center' }}>Allow Taker</div>
              <div style={{ width: 50, textAlign: 'center' }}>TOB</div>
              <div style={{ width: 60, textAlign: 'center' }}>Disable</div>
            </div>

            {this.value.map((it, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                {/* 主内容行 */}
                <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 8 }}>
                  {/* Expiry 输入 */}
                  <div style={{ width: 100, marginRight: 0 }}>
                    <Expiries
                      style={{ width: 100, minWidth: 0 }}
                      multiple={false}
                      options={this.props.options}
                      value={[it.expiry || '']}
                      onChange={(val) => this.onChange(i, val[0], 'expiry')}
                      onBlur={this.props.onBlur}
                      calcDisabled={(option) => !!this.value.find((vega) => vega.expiry === option)}
                    />
                  </div>

                  {/* Vega Target 输入 */}
                  <div style={{ width: 80 }}>
                    <Num
                      value={this.props.value?.[i]?.vegaTarget}
                      onChange={(val) => this.onChange(i, val, 'vegaTarget')}
                      isPercentage={false}
                      readonly={this.props.readonly}
                      onBlur={this.props.onBlur}
                      style={{ height: 30 }}
                      singleWidth={50}
                    />
                  </div>

                  {/* Vega Rules 区域 */}
                  <div style={{ flex: 1, minWidth: 734 }}>
                    {/* Rules 内容 */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {it.vegaRules.map((rule, k) => (
                        <div
                          key={k}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8 }}
                        >
                          {/* Option Type */}
                          <div
                            style={{
                              display: 'flex',
                              width: 120,
                              height: 30,
                              alignItems: 'center',
                            }}
                          >
                            <div
                              style={{
                                visibility: k === it.vegaRules?.length - 1 ? 'visible' : 'hidden',
                              }}
                            >
                              <PlusOutlined
                                onClick={this.onAddRule.bind(null, i, k)}
                                style={{ width: 18 }}
                              />
                            </div>
                            <Select
                              style={{ width: 100 }}
                              value={[rule.optionType || '']}
                              onChange={(val) =>
                                this.onChange(i, val, 'vegaRules', k, 'optionType')
                              }
                              placeholder="Choose"
                              options={['Both', 'Call', 'Put'].map(it => ({ label: it, value: it }))}
                            />
                          </div>

                          {/* Delta Range */}
                          <div style={{ width: 160 }}>
                            <NumRange
                              value={{ min: rule.deltaRange.min, max: rule.deltaRange.max }}
                              onChange={(val) =>
                                this.onChange(i, val, 'vegaRules', k, 'deltaRange')
                              }
                              onBlur={this.props.onBlur}
                              singleWidth={50}
                            />
                          </div>

                          {/* Bid Edge Range */}
                          <div style={{ width: 210 }}>
                            <NumRangeFromTo
                              value={{ from: rule.bidEdgeSpan.from, to: rule.bidEdgeSpan.to }}
                              onChange={(val: any) =>
                                this.onChange(i, val, 'vegaRules', k, 'bidEdgeSpan')
                              }
                              isPercentage
                              onBlur={this.props.onBlur}
                            />
                          </div>

                          {/* Ask Edge Range */}
                          <div style={{ width: 210 }}>
                            <NumRangeFromTo
                              value={{ from: rule.askEdgeSpan.from, to: rule.askEdgeSpan.to }}
                              onChange={(val: any) =>
                                this.onChange(i, val, 'vegaRules', k, 'askEdgeSpan')
                              }
                              isPercentage
                              onBlur={this.props.onBlur}
                            />
                          </div>

                          {/* Allow Taker */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: 74,
                            }}
                          >
                            <Checkbox
                              checked={rule.allowTaker}
                              onChange={(e) =>
                                this.onChange(i, e.target.checked, 'vegaRules', k, 'allowTaker')
                              }
                            />
                          </div>

                          {/* TOB */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: 50,
                            }}
                          >
                            <Checkbox
                              checked={rule.tob}
                              onChange={(e) =>
                                this.onChange(i, e.target.checked, 'vegaRules', k, 'tob')
                              }
                            />
                          </div>

                          {/* Disabled + Delete */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              position: 'relative',
                              width: 60,
                            }}
                          >
                            <Checkbox
                              checked={rule.disable}
                              onChange={(e) =>
                                this.onChange(i, e.target.checked, 'vegaRules', k, 'disable')
                              }
                            />
                            <div
                              style={{
                                position: 'absolute',
                                right: -48,
                                top: -4,
                                visibility: k === 0 ? 'hidden' : 'visible',
                              }}
                            >
                              <DeleteOutlined
                                onClick={this.onDelRule.bind(null, i, k)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: 40, marginTop: 10 }}>
                    <PlusOutlined
                      onClick={this.onAdd.bind(null, i)}
                    />
                    <DeleteOutlined
                      onClick={this.onDel.bind(null, i)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
